import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'designSystem/components/Table'
import { Text } from 'designSystem/components/Text'
import type {
  ReportBlock,
  ReportSelectionAction,
  ReportTableSource,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/reportViewSchema'
import styled from 'styled-components'

const DataTable = styled(Table)`
  th,
  td {
    white-space: normal;
    vertical-align: top;
    overflow-wrap: anywhere;
    min-width: 7rem;
  }
`
const Cards = styled.div`
  display: grid;
  gap: 1rem;
`
const Card = styled.dl`
  margin: 0;
  border: 0.0625rem solid var(--border);
  border-radius: var(--radius);
  padding: 1rem;
  display: grid;
  gap: 0.5rem;
  dt {
    color: var(--muted-foreground);
  }
  dd {
    margin: 0;
  }
`
export function ReportTable({
  source,
  block,
  onSelect,
}: {
  source: ReportTableSource
  block: ReportBlock
  onSelect?: (action: ReportSelectionAction) => void
}) {
  const { t, i18n } = useLingui()
  const columns = block.columns
    ? block.columns.flatMap((id) =>
        source.columns.filter((column) => column.id === id)
      )
    : source.columns
  const value = (
    cell: ReportTableSource['rows'][number][string] | undefined,
    column: ReportTableSource['columns'][number]
  ) => {
    if (
      cell === null ||
      cell === undefined ||
      cell === '' ||
      (Array.isArray(cell) && !cell.length)
    )
      return t`Not recorded`
    if (Array.isArray(cell))
      return (
        <ul>
          {cell.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      )
    return typeof cell === 'number'
      ? i18n.number(cell, {
          maximumFractionDigits: column.maximum_fraction_digits ?? 2,
        })
      : cell
  }
  const label = (column: ReportTableSource['columns'][number]) =>
    column.unit ? `${column.label} (${column.unit})` : column.label
  const actionFor = (index: number) => {
    const action = source.row_actions?.find((item) => item.row_index === index)
    return action && onSelect ? (
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={() => onSelect(action)}>
        {action.label}
      </Button>
    ) : null
  }
  const hasActions = !!onSelect && !!source.row_actions?.length
  return (
    <>
      {!source.rows.length ? (
        <Text color='muted'>
          <Trans>No recorded rows.</Trans>
        </Text>
      ) : block.layout === 'cards' ? (
        <Cards>
          {source.rows.map((row, index) => (
            <Card key={index}>
              {columns.map((column) => (
                <div key={column.id}>
                  <dt>{label(column)}</dt>
                  <dd>{value(row[column.id], column)}</dd>
                </div>
              ))}
              {hasActions && actionFor(index) ? (
                <div>
                  <dt>
                    <Trans>Actions</Trans>
                  </dt>
                  <dd>{actionFor(index)}</dd>
                </div>
              ) : null}
            </Card>
          ))}
        </Cards>
      ) : (
        <DataTable aria-label={block.title || source.title}>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.id}>{label(column)}</TableHead>
              ))}
              {hasActions ? (
                <TableHead>
                  <Trans>Actions</Trans>
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {source.rows.map((row, index) => (
              <TableRow key={index}>
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    {value(row[column.id], column)}
                  </TableCell>
                ))}
                {hasActions ? <TableCell>{actionFor(index)}</TableCell> : null}
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
      )}
      {source.truncated ? (
        <Text variant='caption' color='muted'>
          <Trans>
            This table is truncated. Open the source record for complete
            results.
          </Trans>
        </Text>
      ) : null}
    </>
  )
}
